import {
  Action,
  ActionExample,
  HandlerCallback,
  IAgentRuntime,
  Memory,
  ModelType,
  State,
  logger,
  parseKeyValueXml,
} from '@elizaos/core';
import { ZapperConfig, NFTMetadata, NFTCollectionMetadata, NFTAnalytics } from '../types';
import {
  getZapperClient,
  initializeZapperClient,
  isZapperClientInitialized,
} from '../utils/zapperClient';
import { Content } from '@elizaos/core';

export interface GetNFTCollectionContent extends Content {
  address: string;
  network?: string;
  collection?: string;
  includeAnalytics?: boolean | string;
  includeMetadata?: boolean | string;
  includeItems?: boolean | string;
  includeTraits?: boolean | string;
  limit?: number | string;
  timeframe?: string;
}

const getNFTCollectionTemplate = `Respond with an XML block containing only the extracted values. Use key-value pairs.

Example response for NFT collection request:
<response>
    <address>0x1234567890abcdef1234567890abcdef12345678</address>
    <network>ethereum</network>
    <collection>Bored Ape Yacht Club</collection>
    <includeAnalytics>true</includeAnalytics>
    <includeMetadata>true</includeMetadata>
    <includeItems>false</includeItems>
    <includeTraits>false</includeTraits>
    <limit>20</limit>
    <timeframe>30d</timeframe>
</response>

## Recent Messages

{{recentMessages}}

Given the recent messages, extract the following information about the NFT collection request:
- Address: The wallet address or collection address to analyze
- Network: Optional network name (ethereum, polygon, arbitrum, optimism, base)
- Collection: Optional specific collection name or address
- IncludeAnalytics: Whether to include analytics data like floor price, volume, stats (true/false)
- IncludeMetadata: Whether to include detailed collection metadata (true/false)
- IncludeItems: Whether to include individual NFT items in the collection (true/false)
- IncludeTraits: Whether to include basic trait information (true/false)
- Limit: Number of items to return if including items (default: 20)
- Timeframe: Time period for analytics (7d, 30d, 90d, 1y)

Respond with an XML block containing only the extracted values.`;

export const getNFTCollectionAction: Action = {
  name: 'GET_NFT_COLLECTION',
  similes: [
    'GET_NFTS',
    'CHECK_NFTS',
    'VIEW_NFTS',
    'SHOW_NFTS',
    'GET_NFT_ANALYTICS',
    'GET_NFT_COLLECTION_METADATA',
    'CHECK_COLLECTIBLES',
    'VIEW_COLLECTIBLES',
    'NFT_COLLECTION_INFO',
    'NFT_COLLECTION_STATS',
    'COLLECTION_OVERVIEW',
    'COLLECTION_DASHBOARD',
  ],
  description:
    'Get comprehensive NFT collection data including analytics, metadata, and individual items',
  validate: async (runtime: IAgentRuntime, message: Memory) => {
    logger.debug('Starting GET_NFT_COLLECTION validation');
    logger.debug('Validating GET_NFT_COLLECTION action');

    const hasApiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!hasApiKey) {
      logger.error('ZAPPER_API_KEY not configured');
      return false;
    }

    const text = message.content.text?.toLowerCase() || '';
    logger.debug('Analyzing message text for NFT collection keywords');

    // Specific validation keywords to avoid conflicts
    const nftCollectionKeywords = [
      // Collection-specific keywords
      'nft collection',
      'collection metadata',
      'collection analytics',
      'collection stats',
      'collection info',
      'collection overview',
      'collection dashboard',

      // General NFT keywords (when combined with collection context)
      'show nfts',
      'view nfts',
      'check nfts',
      'my nfts',
      'wallet nfts',
      'nft holdings',

      // Analytics keywords (when NFT context present)
      'nft analytics',
      'nft stats',
      'nft floor price',
      'nft volume',
      'nft metrics',

      // Art/collectible keywords
      'collectible collection',
      'art collection',
      'digital art collection',
    ];

    // Must have NFT context AND keyword match
    const hasNFTContext = /\b(nft|collectible|collection|art|digital art)\b/i.test(text);
    const hasKeyword = nftCollectionKeywords.some((keyword) => text.includes(keyword));

    // Also allow simple NFT queries without specific tokens (for collection overview)
    const isSimpleNFTQuery =
      hasNFTContext && !/\b(#\d+|\d{3,}|token id|specific|price|worth|value|cost)\b/i.test(text);

    const validationResult = (hasNFTContext && hasKeyword) || isSimpleNFTQuery;
    
    logger.debug('Validation result', validationResult);
    
    return validationResult;
  },
  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state: State | undefined,
    _options: { [key: string]: unknown } | undefined,
    callback?: HandlerCallback
  ) => {
    logger.debug('Starting GET_NFT_COLLECTION handler');

    let currentState = state;
    if (!currentState) {
      currentState = await runtime.composeState(message);
    } else {
      currentState = await runtime.composeState(message, ['RECENT_MESSAGES']);
    }

    const prompt = getNFTCollectionTemplate.replace('{{recentMessages}}', currentState.recentMessagesData || '');

    const result = await runtime.useModel(ModelType.TEXT_SMALL, {
      prompt,
      stopSequences: [],
    });

    const content = parseKeyValueXml(result);
    logger.debug('Parsed content:', content);

    if (!isGetNFTCollectionContent(runtime, content)) {
      logger.error('Invalid content for GET_NFT_COLLECTION action.');
      callback?.({
        text: 'Unable to process NFT collection request. Please provide a valid wallet address or collection name.',
        content: { error: 'Invalid content' },
      });
      return;
    }

    try {
      const zapperConfig: ZapperConfig = {
        apiKey: runtime.getSetting('ZAPPER_API_KEY'),
        apiUrl: runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql',
      };

      const network = content.network || 'ethereum';
      const includeAnalytics =
        content.includeAnalytics === 'true' || content.includeAnalytics === true;
      const includeMetadata =
        content.includeMetadata === 'true' || content.includeMetadata === true;
      const includeItems = content.includeItems === 'true' || content.includeItems === true;
      const includeTraits = content.includeTraits === 'true' || content.includeTraits === true;
      const limit = Number(content.limit) || 20;
      const timeframe = content.timeframe || '30d';

      // Fetch comprehensive NFT collection data
      const collectionData = await fetchNFTCollectionData(
        zapperConfig,
        content.address,
        content.collection,
        network,
        {
          includeAnalytics,
          includeMetadata,
          includeItems,
          includeTraits,
          limit,
          timeframe,
        }
      );

      if (!collectionData) {
        callback?.({
          text: `No NFT collection data found for ${content.address} on ${network}`,
          content: {
            address: content.address,
            collection: content.collection,
            network,
            error: 'No data found',
          },
        });
        return;
      }

      // Format comprehensive response
      let responseText = `🖼️ NFT Collection Data`;

      if (content.collection) {
        responseText += ` - ${content.collection}`;
      }

      responseText += `:\n\n`;

      // Collection Overview
      responseText += `📍 **Overview**:\n`;
      responseText += `• Address: ${content.address}\n`;
      responseText += `• Network: ${network}\n`;
      if (collectionData.items) {
        responseText += `• Total Items: ${collectionData.items.length}\n`;
      }

      // Analytics section (if requested)
      if (includeAnalytics && collectionData.analytics) {
        responseText += `\n📊 **Analytics** (${timeframe}):\n`;
        if (collectionData.analytics.floorPriceUSD) {
          responseText += `• Floor Price: $${collectionData.analytics.floorPriceUSD.toFixed(4)}\n`;
        }
        if (collectionData.analytics.totalVolumeUSD) {
          responseText += `• Total Volume: $${(collectionData.analytics.totalVolumeUSD / 1e6).toFixed(2)}M\n`;
        }
        if (collectionData.analytics.owners) {
          responseText += `• Unique Owners: ${collectionData.analytics.owners.toLocaleString()}\n`;
        }
        if (collectionData.analytics.sales24h) {
          responseText += `• 24h Sales: ${collectionData.analytics.sales24h}\n`;
        }
        if (collectionData.analytics.salesVolume24h) {
          responseText += `• 24h Volume: $${(collectionData.analytics.salesVolume24h / 1e3).toFixed(1)}K\n`;
        }
      }

      // Metadata section (if requested)
      if (includeMetadata && collectionData.metadata) {
        responseText += `\n📋 **Collection Metadata**:\n`;
        if (collectionData.metadata.name) {
          responseText += `• Name: ${collectionData.metadata.name}\n`;
        }
        if (collectionData.metadata.description) {
          responseText += `• Description: ${collectionData.metadata.description.substring(0, 200)}...\n`;
        }
        if (collectionData.metadata.totalSupply) {
          responseText += `• Total Supply: ${collectionData.metadata.totalSupply.toLocaleString()}\n`;
        }
        if (collectionData.metadata.verified) {
          responseText += `• Verified: ✅\n`;
        }
        if (collectionData.metadata.website) {
          responseText += `• Website: ${collectionData.metadata.website}\n`;
        }
      }

      // Items section (if requested)
      if (includeItems && collectionData.items && collectionData.items.length > 0) {
        responseText += `\n🖼️ **Sample Items** (showing ${Math.min(5, collectionData.items.length)} of ${collectionData.items.length}):\n`;
        collectionData.items.slice(0, 5).forEach((item, index) => {
          responseText += `${index + 1}. ${item.name || `Token #${item.tokenId}`}`;
          if (item.estimatedValue) {
            responseText += ` (~$${item.estimatedValue.toFixed(2)})`;
          }
          responseText += `\n`;

          if (includeTraits && item.traits && item.traits.length > 0) {
            const topTraits = item.traits.slice(0, 3);
            responseText += `   Traits: ${topTraits.map((t) => `${t.trait_type}: ${t.value}`).join(', ')}\n`;
          }
        });
      }

      callback?.({
        text: responseText,
        content: {
          action: 'GET_NFT_COLLECTION',
          address: content.address,
          collection: content.collection,
          network,
          totalItems: collectionData.items?.length || 0,
          analytics: collectionData.analytics,
          metadata: collectionData.metadata,
          items: collectionData.items?.slice(0, limit),
          options: {
            includeAnalytics,
            includeMetadata,
            includeItems,
            includeTraits,
            limit,
            timeframe,
          },
        },
      });
    } catch (error) {
      logger.error('NFT collection fetch failed:', error);
      callback?.({
        text: 'Failed to fetch NFT collection data. Please try again.',
        content: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  },
  examples: [
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show me my NFT collection with analytics',
        },
      },
      {
        name: '{{user2}}',
        content: {
          text: 'NFT collection data with analytics retrieved',
          action: 'GET_NFT_COLLECTION',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Get Bored Ape Yacht Club collection metadata and floor price',
        },
      },
      {
        name: '{{user2}}',
        content: {
          text: 'Collection metadata and analytics retrieved',
          action: 'GET_NFT_COLLECTION',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'View my collectibles with traits and metadata',
        },
      },
      {
        name: '{{user2}}',
        content: {
          text: 'Collectible collection with traits and metadata retrieved',
          action: 'GET_NFT_COLLECTION',
        },
      },
    ],
  ],
};

function isGetNFTCollectionContent(
  _runtime: IAgentRuntime,
  content: any
): content is GetNFTCollectionContent {
  logger.debug('Content for validation', content);
  return (
    content &&
    typeof content.address === 'string' &&
    content.address.length > 0 &&
    (content.network === undefined || typeof content.network === 'string') &&
    (content.collection === undefined || typeof content.collection === 'string') &&
    (content.includeAnalytics === undefined ||
      typeof content.includeAnalytics === 'boolean' ||
      content.includeAnalytics === 'true' ||
      content.includeAnalytics === 'false') &&
    (content.includeMetadata === undefined ||
      typeof content.includeMetadata === 'boolean' ||
      content.includeMetadata === 'true' ||
      content.includeMetadata === 'false') &&
    (content.includeItems === undefined ||
      typeof content.includeItems === 'boolean' ||
      content.includeItems === 'true' ||
      content.includeItems === 'false') &&
    (content.includeTraits === undefined ||
      typeof content.includeTraits === 'boolean' ||
      content.includeTraits === 'true' ||
      content.includeTraits === 'false') &&
    (content.limit === undefined ||
      typeof content.limit === 'number' ||
      typeof content.limit === 'string') &&
    (content.timeframe === undefined || typeof content.timeframe === 'string')
  );
}

interface NFTCollectionData {
  analytics?: NFTAnalytics;
  metadata?: NFTCollectionMetadata;
  items?: NFTMetadata[];
}

async function fetchNFTCollectionData(
  config: ZapperConfig,
  address: string,
  collection: string | undefined,
  network: string,
  options: {
    includeAnalytics?: boolean;
    includeMetadata?: boolean;
    includeItems?: boolean;
    includeTraits?: boolean;
    limit?: number;
    timeframe?: string;
  }
): Promise<NFTCollectionData | null> {
  // Initialize Zapper client for real API calls
  if (!isZapperClientInitialized()) {
    initializeZapperClient(config.apiKey, config.apiUrl);
  }

  const client = getZapperClient();

  // Map network names to chain IDs
  const networkToChainId: { [key: string]: number } = {
    ethereum: 1,
    polygon: 137,
    arbitrum: 42161,
    optimism: 10,
    base: 8453,
    avalanche: 43114,
    'binance-smart-chain': 56,
    bsc: 56,
    fantom: 250,
    mainnet: 1,
    eth: 1,
    matic: 137,
    arb: 42161,
    op: 10,
    avax: 43114,
    bnb: 56,
    ftm: 250,
  };

  const chainId = networkToChainId[network.toLowerCase()] || 1;

  // Use real Zapper GraphQL API to get NFT data
  const query = `
    query NFTCollectionData($addresses: [Address!]!, $chainIds: [Int!]) {
      portfolioV2(addresses: $addresses, chainIds: $chainIds) {
        nftBalances(
          withOverrides: false
          waitForUpdate: true
        ) {
          byToken(first: ${Math.min(options.limit || 5, 5)}) {
            edges {
              node {
                tokenId
                balanceUSD
                token {
                  id
                  tokenId
                  collection {
                    id
                    address
                    name
                  }
                  metadata {
                    uri
                    data
                  }
                }
              }
            }
          }
          totalBalanceUSD
          totalTokensOwned
          distinctTokensOwned
        }
      }
    }
  `;

  const variables = {
    addresses: [address],
    chainIds: [chainId],
  };

  logger.debug('NFT collection API request:', {
    variables,
    options,
  });

  try {
    // Validate address format
    if (!address || !/^0x[a-fA-F0-9]{40}$/.test(address)) {
      logger.error('Invalid Ethereum address format:', address);
      throw new Error('Invalid address format - must be valid Ethereum address');
    }

    // Use real Zapper client with proper caching and error handling
    const result = await client.query(query, variables, {
      cacheTTL: 600000, // 10-minute cache for NFT data
      useCache: true,
      timeout: 30000,
      retryAttempts: 2,
    });

    if (result.errors) {
      logger.error('NFT collection API GraphQL errors:', result.errors);

      // Check for specific error types
      const errorMessages = result.errors.map((e: any) => e.message).join(', ');
      if (errorMessages.includes('401') || errorMessages.includes('Unauthorized')) {
        throw new Error('Invalid or missing Zapper API key');
      }
      if (errorMessages.includes('429') || errorMessages.includes('rate limit')) {
        throw new Error('Rate limit exceeded - please try again later');
      }
      if (errorMessages.includes('400') || errorMessages.includes('Bad Request')) {
        throw new Error('Invalid query parameters or address format');
      }

      throw new Error(`GraphQL errors: ${errorMessages}`);
    }

    // Process real NFT data from Zapper API - correct schema with validation
    logger.debug('🔍 Processing API response data');
    const portfolioData = result.data?.portfolioV2;
    if (!portfolioData) {
      logger.warn('⚠️ No portfolio data in API response', {
        hasResultData: !!result.data,
        resultDataKeys: result.data ? Object.keys(result.data) : []
      });
      return null;
    }
    logger.debug('✅ Portfolio data found in response');

    const nftBalances = portfolioData.nftBalances;
    if (!nftBalances) {
      logger.warn('⚠️ No NFT balances in portfolio data', {
        address,
        chainId,
        network,
        portfolioDataKeys: Object.keys(portfolioData)
      });
      return null;
    }
    logger.debug('✅ NFT balances found', {
      totalBalanceUSD: nftBalances.totalBalanceUSD,
      totalTokensOwned: nftBalances.totalTokensOwned
    });

    const nftEdges = nftBalances.byToken?.edges;
    if (!Array.isArray(nftEdges) || nftEdges.length === 0) {
      logger.warn('⚠️ No NFT tokens found in balances', {
        address,
        chainId,
        network,
        hasByToken: !!nftBalances.byToken,
        edgesType: typeof nftEdges,
        edgesLength: Array.isArray(nftEdges) ? nftEdges.length : 'not array'
      });
      return null;
    }
    logger.info('✅ NFT tokens found', {
      tokenCount: nftEdges.length,
      requestedLimit: options.limit
    });

    logger.debug('🏗️ Building NFT collection data structure');
    const data: NFTCollectionData = {};

    // Process NFT items from real API data with error handling
    if (options.includeItems) {
      logger.debug('🖼️ Processing NFT items', {
        edgeCount: nftEdges.length,
        limit: options.limit
      });
      try {
        data.items = nftEdges
          .slice(0, options.limit || 25)
          .map((edge: any, index: number) => {
            if (!edge?.node?.token) {
              logger.warn('⚠️ Invalid NFT edge structure', {
                edgeIndex: index,
                hasEdge: !!edge,
                hasNode: !!edge?.node,
                hasToken: !!edge?.node?.token
              });
              return null;
            }

            const node = edge.node;
            const token = node.token;

            // Parse metadata for name and image if available
            logger.debug('🔍 Parsing NFT metadata', {
              tokenId: token.tokenId,
              hasMetadata: !!token.metadata,
              hasMetadataData: !!token.metadata?.data
            });
            
            let parsedName = `Token #${token.tokenId}`;
            let parsedImage = '';

            if (token.metadata?.data) {
              try {
                const metadata = JSON.parse(token.metadata.data);
                parsedName = metadata.name || parsedName;
                parsedImage = metadata.image || metadata.image_url || '';
                logger.debug('✅ Metadata parsed successfully', {
                  tokenId: token.tokenId,
                  parsedName,
                  hasImage: !!parsedImage
                });
              } catch (e) {
                logger.debug('⚠️ Failed to parse NFT metadata JSON', {
                  tokenId: token.tokenId,
                  error: e instanceof Error ? e.message : String(e)
                });
              }
            }

            const item: NFTMetadata = {
              name: parsedName,
              description: '', // Could be parsed from metadata.data if needed
              image: parsedImage,
              collection: token.collection?.name || collection || 'Unknown Collection',
              tokenId: token.tokenId || '',
              network: network, // Network info not available at token level in this query
              estimatedValue: node.balanceUSD || 0,
              contractAddress: token.collection?.address || '',
            };
            return item;
          })
          .filter((item): item is NFTMetadata => item !== null); // Type-safe filter
          
        logger.info('✅ NFT items processed successfully', {
          totalEdges: nftEdges.length,
          processedItems: data.items?.length || 0,
          nullItems: nftEdges.length - (data.items?.length || 0)
        });
      } catch (itemError) {
        logger.error('❌ Error processing NFT items', {
          error: itemError instanceof Error ? itemError.message : String(itemError),
          edgeCount: nftEdges.length
        });
        data.items = []; // Fallback to empty array
      }
    }

    // Generate analytics from available data with safe parsing
    if (options.includeAnalytics) {
      logger.debug('📊 Generating analytics data');
      try {
        const totalValueUSD =
          typeof nftBalances.totalBalanceUSD === 'number' ? nftBalances.totalBalanceUSD : 0;
        const totalTokensRaw = nftBalances?.totalTokensOwned || '0';
        const distinctTokensRaw = nftBalances?.distinctTokensOwned || '0';

        // Safe parsing with fallbacks
        const totalTokens = parseInt(totalTokensRaw, 10) || 0;
        const distinctTokens = parseInt(distinctTokensRaw, 10) || 0;

        data.analytics = {
          collection: collection || 'NFT Collection',
          floorPriceUSD: totalTokens > 0 ? totalValueUSD / totalTokens : 0,
          totalVolumeUSD: totalValueUSD,
          totalSupply: totalTokens,
          owners: distinctTokens > 0 ? distinctTokens : undefined,
          topSales: [],
          holderDistribution: [],
        };
        
        logger.info('✅ Analytics generated successfully', {
          totalValueUSD,
          totalTokens,
          distinctTokens,
          floorPriceUSD: data.analytics.floorPriceUSD
        });
      } catch (analyticsError) {
        logger.error('❌ Error generating analytics', {
          error: analyticsError instanceof Error ? analyticsError.message : String(analyticsError),
          hasNftBalances: !!nftBalances
        });
        // Provide minimal analytics on error
        data.analytics = {
          collection: collection || 'NFT Collection',
          topSales: [],
          holderDistribution: [],
        };
      }
    }

    // Generate metadata from available data with error handling
    if (options.includeMetadata && nftEdges.length > 0) {
      logger.debug('📋 Generating metadata from first token');
      try {
        const firstToken = nftEdges[0]?.node?.token;
        const collectionInfo = firstToken?.collection;
        const totalSupplyRaw = nftBalances?.totalTokensOwned || '0';

        data.metadata = {
          name: collectionInfo?.name || collection || 'NFT Collection',
          description: 'NFT collection data retrieved from Zapper Protocol',
          totalSupply: parseInt(totalSupplyRaw, 10) || 0,
          verified: Boolean(collectionInfo?.verified),
        };
        
        logger.info('✅ Metadata generated successfully', {
          name: data.metadata.name,
          totalSupply: data.metadata.totalSupply,
          verified: data.metadata.verified
        });
      } catch (metadataError) {
        logger.error('❌ Error generating metadata', {
          error: metadataError instanceof Error ? metadataError.message : String(metadataError),
          hasFirstToken: !!nftEdges[0]?.node?.token
        });
        // Provide minimal metadata on error
        data.metadata = {
          name: collection || 'NFT Collection',
          description: 'NFT collection data retrieved from Zapper Protocol',
          totalSupply: 0,
          verified: false,
        };
      }
    }

    logger.info('🎉 NFT collection data fetch completed successfully', {
      hasAnalytics: !!data.analytics,
      hasMetadata: !!data.metadata,
      itemCount: data.items?.length || 0,
      address,
      network,
      chainId
    });
    
    return data;
  } catch (error) {
    logger.error('❌ Critical error in NFT collection data fetch', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : 'No stack trace',
      address,
      network,
      chainId,
      collection
    });
    throw error;
  }
}
